import { Menu, Transition } from '@headlessui/react'
import { ChevronUpDownIcon, UserIcon } from '@heroicons/react/24/outline'
import { Fragment, useEffect, useState } from 'react'
import { getEmail } from '../../util/branding'
import { classNames } from '../../util/classNames'

type Props = {
    signOut: () => void
    sidebarOpen?: boolean
}

/**
 * Account menu pinned to the foot of the sidebar. The menu opens upward
 * (bottom-full) so it stays on screen. When the rail is collapsed it shrinks to
 * the avatar only, and the dropdown widens so it escapes the narrow rail.
 */
function ProfileButton(props: Props) {
    const [email, setEmail] = useState('')
    useEffect(() => {
        getEmail(setEmail)
    }, [])

    const { signOut, sidebarOpen = true } = props
    return (
        <Menu as="div" className="relative" aria-label="Profile menu">
            <Menu.Button
                className={classNames(
                    sidebarOpen ? 'gap-2.5 px-3' : 'justify-center px-0',
                    // overflow-hidden: during the sidebar expand the open-state content
                    // (avatar + email + chevron) renders while the rail is still narrow;
                    // clipping here stops it overflowing the rail and flashing a
                    // scrollbar. The dropdown (Menu.Items) is a sibling, so it still escapes.
                    'flex w-full items-center overflow-hidden rounded-md py-2 text-left text-sm text-ink-700 hover:bg-ink-50 focus:outline-none focus:ring-2 focus:ring-arwen-500'
                )}
                aria-label="profile button"
                data-cy="profile_button"
                title={!sidebarOpen ? email || 'Account' : undefined}
            >
                <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-ink-100 text-ink-700">
                    <UserIcon className="h-4 w-4" aria-hidden="true" />
                </span>
                {sidebarOpen && (
                    <>
                        <span className="min-w-0 flex-1 truncate">{email || 'Account'}</span>
                        <ChevronUpDownIcon
                            className="h-4 w-4 flex-none text-ink-400"
                            aria-hidden="true"
                        />
                    </>
                )}
            </Menu.Button>

            <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
            >
                <Menu.Items className="absolute bottom-full left-0 z-50 mb-2 w-56 origin-bottom divide-y divide-ink-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <div className="truncate px-4 py-3 text-sm font-medium text-ink-700">
                        {email}
                    </div>
                    <div className="py-1">
                        <Menu.Item>
                            {({ active }) => (
                                <button
                                    onClick={signOut}
                                    aria-label="Sign out"
                                    data-cy="sign_out_button"
                                    className={classNames(
                                        active ? 'bg-ink-50' : '',
                                        'block w-full px-4 py-2 text-left text-sm text-arwen-700'
                                    )}
                                >
                                    Sign out
                                </button>
                            )}
                        </Menu.Item>
                    </div>
                </Menu.Items>
            </Transition>
        </Menu>
    )
}

export default ProfileButton
